import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiCheckCircle, FiSend, FiUser, FiCalendar, FiMapPin, FiBriefcase } from 'react-icons/fi';

const Bewerber = () => {
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setTimeout(() => {
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 800);
  };

  return (
    <div className="bg-slate-50 min-h-screen py-16 md:py-24">
      <div className="container-custom">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <motion.h1 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-6xl font-extrabold mb-6"
          >
            Dein Weg zum <span className="text-teal-600">Nebenjob</span>
          </motion.h1>
          <p className="text-xl text-slate-600">
            Wir machen Bewerben so einfach wie Texten. In 2 Minuten bist du fertig.
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <AnimatePresence mode="wait">
            {submitted ? (
              <motion.div 
                key="success"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-white p-12 rounded-[40px] shadow-2xl text-center border border-teal-100"
              >
                <div className="w-24 h-24 bg-teal-50 text-teal-600 rounded-full flex items-center justify-center mx-auto mb-8">
                  <SafeIcon icon={FiCheckCircle} className="text-5xl" />
                </div>
                <h2 className="text-4xl font-bold mb-4">Moin! Das hat geklappt.</h2>
                <p className="text-slate-600 text-lg mb-10 max-w-md mx-auto">
                  Deine Daten sind sicher bei uns gelandet. Wir prüfen jetzt die passenden Jobs in Hamburg und melden uns bei dir.
                </p>
                <button 
                  onClick={() => setSubmitted(false)}
                  className="btn-secondary"
                >
                  Zurück zur Übersicht
                </button>
              </motion.div>
            ) : (
              <motion.div 
                key="form"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-[40px] shadow-2xl shadow-slate-200/60 overflow-hidden border border-slate-100"
              >
                <div className="p-8 md:p-12">
                  <form onSubmit={handleSubmit} className="space-y-12">
                    {/* Step 1: Who are you */}
                    <section>
                      <div className="flex items-center gap-4 mb-8">
                        <div className="w-10 h-10 rounded-full bg-teal-600 text-white flex items-center justify-center font-bold">1</div>
                        <h2 className="text-2xl font-bold">Wer bist du?</h2>
                      </div>
                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-1.5">
                          <label className="text-sm font-bold text-slate-700 ml-1">Vorname</label>
                          <input type="text" required placeholder="Max" className="input-field" />
                        </div>
                        <div className="space-y-1.5">
                          <label className="text-sm font-bold text-slate-700 ml-1">Nachname</label>
                          <input type="text" required placeholder="Mustermann" className="input-field" />
                        </div>
                        <div className="space-y-1.5 md:col-span-2">
                          <label className="text-sm font-bold text-slate-700 ml-1">E-Mail</label>
                          <input type="email" required placeholder="max@beispiel.de" className="input-field" />
                        </div>
                      </div>
                    </section>

                    {/* Step 2: What & Where */}
                    <section>
                      <div className="flex items-center gap-4 mb-8">
                        <div className="w-10 h-10 rounded-full bg-teal-600 text-white flex items-center justify-center font-bold">2</div>
                        <h2 className="text-2xl font-bold">Job & Verfügbarkeit</h2>
                      </div>
                      
                      <div className="space-y-8">
                        <div className="space-y-3">
                          <label className="text-sm font-bold text-slate-700 ml-1">Was interessiert dich?</label>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                            {['Aushilfe', 'Büro', 'Home Office', 'Reinigung', 'Gastro', 'Lager'].map(job => (
                              <label key={job} className="relative group cursor-pointer">
                                <input type="checkbox" className="peer sr-only" />
                                <div className="p-4 border border-slate-200 rounded-2xl text-sm font-semibold text-slate-600 peer-checked:border-teal-600 peer-checked:bg-teal-50 peer-checked:text-teal-700 transition-all group-hover:bg-slate-50">
                                  {job}
                                </div>
                              </label>
                            ))}
                          </div>
                        </div>

                        <div className="grid md:grid-cols-2 gap-6">
                          <div className="space-y-1.5">
                            <label className="text-sm font-bold text-slate-700 ml-1">Stadtteil / PLZ</label>
                            <div className="relative">
                              <SafeIcon icon={FiMapPin} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                              <input type="text" required placeholder="z.B. Altona" className="input-field pl-11" />
                            </div>
                          </div>
                          <div className="space-y-1.5">
                            <label className="text-sm font-bold text-slate-700 ml-1">Verfügbarkeit</label>
                            <div className="relative">
                              <SafeIcon icon={FiCalendar} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                              <select className="input-field pl-11 appearance-none bg-white">
                                <option>Ab sofort</option>
                                <option>In 2 Wochen</option>
                                <option>Nächster Monat</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      </div>
                    </section>

                    {/* Step 3: Privacy */}
                    <section className="pt-8 border-t border-slate-100">
                      <label className="flex items-start gap-4 cursor-pointer group">
                        <div className="mt-1">
                          <input type="checkbox" required className="w-5 h-5 rounded border-slate-300 text-teal-600 focus:ring-teal-500" />
                        </div>
                        <span className="text-sm text-slate-500 leading-relaxed">
                          Ich bin damit einverstanden, dass Nebenjob Nord meine Daten speichert und mich per E-Mail bezüglich passender Jobangebote kontaktiert. Diese Einwilligung kann ich jederzeit widerrufen.
                        </span>
                      </label>
                    </section>

                    <button type="submit" className="w-full btn-primary !py-5 !text-lg">
                      Kostenlos & unverbindlich bewerben
                      <SafeIcon icon={FiSend} className="ml-3" />
                    </button>
                  </form>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default Bewerber;